import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useAuth } from "../../contexts/AuthContext";
import { BACKEND_URL } from "../../utils/constants";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "fdc", label: "FDC" },
  { value: "gun_commander", label: "Gun Commander" },
];

const ROLE_COLORS = {
  admin: "error",
  fdc: "primary",
  gun_commander: "success",
};

const EMPTY_FORM = { username: "", password: "", role: "gun_commander", display_name: "" };

export default function UserManagementPanel() {
  const theme = useTheme();
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // "create" | "edit"
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/auth/users`, { headers, timeout: 10000 });
      setUsers(res.data?.users || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreateDialog = () => {
    setForm(EMPTY_FORM);
    setDialogMode("create");
    setEditingUser(null);
    setDialogOpen(true);
    setError("");
    setSuccess("");
  };

  const openEditDialog = (u) => {
    setForm({ username: u.username, password: "", role: u.role, display_name: u.display_name || "" });
    setDialogMode("edit");
    setEditingUser(u);
    setDialogOpen(true);
    setError("");
    setSuccess("");
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    try {
      if (dialogMode === "create") {
        if (!form.username.trim() || !form.password) {
          setError("Username and password are required");
          return;
        }
        await axios.post(`${BACKEND_URL}/auth/users`, {
          username: form.username.trim(),
          password: form.password,
          role: form.role,
          display_name: form.display_name.trim() || null,
        }, { headers, timeout: 10000 });
        setSuccess(`User "${form.username}" created`);
      } else {
        const payload = {};
        if (form.role !== editingUser.role) payload.role = form.role;
        if (form.display_name.trim() && form.display_name.trim() !== editingUser.display_name) payload.display_name = form.display_name.trim();
        if (form.password) payload.password = form.password;
        if (Object.keys(payload).length === 0) {
          closeDialog();
          return;
        }
        await axios.put(`${BACKEND_URL}/auth/users/${editingUser.id}`, payload, { headers, timeout: 10000 });
        setSuccess(`User "${editingUser.username}" updated`);
      }
      closeDialog();
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    setError("");
    setSuccess("");
    try {
      await axios.delete(`${BACKEND_URL}/auth/users/${u.id}`, { headers, timeout: 10000 });
      setSuccess(`User "${u.username}" deleted`);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user");
    }
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>User Management</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={openCreateDialog}
          sx={{
            borderRadius: 999, textTransform: "none", fontWeight: 700,
            borderColor: alpha(theme.palette.text.primary, 0.2),
          }}
        >
          Add User
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1, fontWeight: 700 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1, fontWeight: 700 }}>{success}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.8) }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.03) }}>
              <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !users.length ? (
              <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", color: "text.secondary" }}>Loading...</TableCell></TableRow>
            ) : !users.length ? (
              <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", color: "text.secondary" }}>No users found</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ fontWeight: 700 }}>{u.username}</TableCell>
                  <TableCell>{u.display_name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ROLES.find((r) => r.value === u.role)?.label || u.role}
                      color={ROLE_COLORS[u.role] || "default"}
                      variant="outlined"
                      sx={{ fontWeight: 700, borderRadius: 999 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditDialog(u)} title="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(u)}
                      disabled={u.id === currentUser?.id}
                      title={u.id === currentUser?.id ? "Cannot delete yourself" : "Delete"}
                      sx={{ color: u.id === currentUser?.id ? "text.disabled" : "error.main" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {dialogMode === "create" ? "Create User" : `Edit ${editingUser?.username}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ fontWeight: 700 }}>{error}</Alert>}
            {dialogMode === "create" && (
              <TextField
                label="Username" size="small" fullWidth autoFocus
                value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              />
            )}
            <TextField
              label="Display Name" size="small" fullWidth
              value={form.display_name} onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
                {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label={dialogMode === "create" ? "Password" : "New Password (leave blank to keep)"}
              type="password" size="small" fullWidth
              value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSubmit}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: 999,
              bgcolor: isDark ? "#a6b078" : "#556b2f", color: isDark ? "#11150f" : "#f7f7f2",
              "&:hover": { bgcolor: isDark ? "#b7c58a" : "#465a27" },
            }}
          >
            {dialogMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
